import React from "react";
import PropTypes from "prop-types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Card, TextInput, Trash01, Plus } from "@opendate-app/setlist";
import RichEditor from "../RichEditor/components/RichEditor";
import DragHandle from "./DragHandle";
import { newSection, sectionHeading } from "./templateSections";
import sortableAnnouncements, { SORTABLE_INSTRUCTIONS } from "./sortableAnnouncements";
import useFocusAfterRemove from "./useFocusAfterRemove";
import "./classesWizard.css";
import "./templateSections.css";

function SortableSectionRow({ section, index, headingError, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const label = section.heading.trim() || `section ${index + 1}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`classes-template-section${isDragging || transform ? " classes-template-section--moving" : ""}`}
      data-testid="template-section-row"
      role="group"
      aria-label={`Section ${index + 1}`}
    >
      <Card padding="md">
        <div className="classes-template-section__header">
          <DragHandle label={`Reorder ${label}`} {...attributes} {...listeners} />
          <div className="classes-template-section__heading">
            <TextInput
              data-field={`section-${section.key}`}
              label="Heading"
              error={headingError}
              placeholder={section.suggestedHeading || "e.g. What to bring"}
              value={section.heading}
              onChange={(e) => onChange(section.key, "heading", e.target.value)}
            />
          </div>
          <span className="classes-danger-icon">
            <Button variant="tertiary" size="sm" icon={Trash01} aria-label={`Remove ${label}`} data-section-remove onClick={() => onRemove(section.key, index)} />
          </span>
        </div>
        <RichEditor
          editorState={section.editorState}
          onEditorStateChange={(editorState) => onChange(section.key, "editorState", editorState)}
          placeholder="Bullets, links and short paragraphs work well here."
          ariaLabel={`${label} body`}
          wrapperClassName="classes-template-section__body"
        />
      </Card>
    </div>
  );
}

SortableSectionRow.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  headingError: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

SortableSectionRow.defaultProps = { headingError: null };

function TemplateSectionsEditor({ sections, fieldErrors, onChange }) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const changeSection = (key, field, value) =>
    onChange((prev) => prev.map((section) => (section.key === key ? { ...section, [field]: value } : section)));
  const focusAfterRemove = useFocusAfterRemove();
  const removeSection = (key, index) => {
    focusAfterRemove("[data-section-remove]", index, "[data-section-add]");
    onChange((prev) => prev.filter((section) => section.key !== key));
  };
  const keys = sections.map((section) => section.key);
  const labelFor = (key) => {
    const index = keys.indexOf(key);
    const heading = index >= 0 && sectionHeading(sections[index]);
    return heading ? `"${heading}"` : `section ${index + 1}`;
  };
  const addSection = () => onChange((prev) => [...prev, newSection()]);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onChange((prev) => {
      const oldIndex = prev.findIndex((section) => section.key === active.id);
      const newIndex = prev.findIndex((section) => section.key === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  return (
    <div className="classes-template-sections">
      {sections.length === 0 && (
        <div className="classes-wizard__setting-hint">
          No page sections yet. Add one to show a card like "What you'll learn" on the public class page.
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        accessibility={{ announcements: sortableAnnouncements(keys, labelFor), screenReaderInstructions: SORTABLE_INSTRUCTIONS }}
      >
        <SortableContext items={keys} strategy={verticalListSortingStrategy}>
          {sections.map((section, index) => (
            <SortableSectionRow
              key={section.key}
              section={section}
              index={index}
              headingError={fieldErrors[`section-${section.key}`]}
              onChange={changeSection}
              onRemove={removeSection}
            />
          ))}
        </SortableContext>
      </DndContext>
      <div>
        <Button variant="secondary" icon={Plus} data-section-add onClick={addSection}>Add Section</Button>
      </div>
    </div>
  );
}

TemplateSectionsEditor.propTypes = {
  sections: PropTypes.array.isRequired,
  fieldErrors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

TemplateSectionsEditor.defaultProps = { fieldErrors: {} };

export default TemplateSectionsEditor;
